import React from 'react';
import { Languages, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type IndianLanguageCode = 'hi' | 'ta' | 'te';

type LanguageDiscoveryBannerProps = {
  onSelectLanguage: (language: IndianLanguageCode) => void;
  onDismiss: () => void;
};

const languageOptions: Array<{ code: IndianLanguageCode; label: string }> = [
  { code: 'hi', label: 'हिन्दी' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
];

const LanguageDiscoveryBanner: React.FC<LanguageDiscoveryBannerProps> = ({
  onDismiss,
  onSelectLanguage,
}) => {
  const { t } = useTranslation();

  return (
    <div className="sl-language-discovery-wrap mb-5 w-full max-w-[42rem] px-4 sm:px-0">
      <section
        className="sl-language-discovery-banner flex items-center gap-2 rounded-lg border border-amber-200/80 bg-white/95 px-3 py-2.5 text-sm text-slate-700 shadow-[0_16px_45px_-28px_rgba(15,23,42,0.45)] ring-1 ring-slate-950/[0.02] dark:border-amber-500/20 dark:bg-slate-900/95 dark:text-slate-200 dark:ring-white/[0.03] sm:gap-3 sm:px-4 sm:py-2"
        aria-label={t('chat.language_discovery.aria_label', 'Indian language options')}
      >
        <div className="sl-language-discovery-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 shadow-sm dark:bg-amber-500/15 dark:text-amber-300">
          <Languages className="h-4 w-4" aria-hidden="true" />
        </div>

        <div className="sl-language-discovery-copy hidden min-w-0 flex-1 truncate font-medium text-slate-700 dark:text-slate-200 sm:block">
          {t('chat.language_discovery.title', 'Sarvalok can answer in your language')}
        </div>

        <div className="sl-language-discovery-options flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto sm:flex-none sm:overflow-visible">
          {languageOptions.map((language) => (
            <button
              key={language.code}
              type="button"
              lang={language.code}
              onClick={() => onSelectLanguage(language.code)}
              className="sl-language-discovery-chip inline-flex shrink-0 items-center rounded-md border border-amber-200/80 bg-white/95 px-2.5 py-1 text-[13px] font-medium text-amber-800 transition-colors hover:bg-amber-50 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 dark:border-amber-500/30 dark:bg-slate-900 dark:text-amber-200 dark:hover:bg-amber-500/10 dark:focus-visible:ring-offset-slate-900"
            >
              {language.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onDismiss}
          className="sl-language-discovery-dismiss shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          aria-label={t('chat.language_discovery.dismiss', 'Dismiss language options permanently')}
          title={t('chat.language_discovery.dismiss', 'Dismiss language options permanently')}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </section>
    </div>
  );
};

export default LanguageDiscoveryBanner;
